import type { UTCTimestamp } from "lightweight-charts";
import pDebounce from "p-debounce";
import type {
	IChartingLibraryWidget,
	ResolutionString,
	UndoRedoState,
} from "@/charting_library/charting_library";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { ONE_YEAR_IN_MS } from "@/lib/utils";
import {
	DEFAULT_STATE_VALUES,
	DEFAULT_TICKER,
} from "@/shared/constants/common";
import { chartColors } from "../../../../const";
import { TIMEFRAME_MAP } from "../../../../services/PriceService/PriceService";
import type { ChartState, Timeframes } from "../../../../types";
import { getResolutionKeyByValue, ResolutionMap } from "../../datafeed";
import type { LibraryChartState } from "../../types";
import { createTimingMomentumText, drawEMReports } from "../features/features";
import {
	filterStudiesByPanelValues,
	removeAllShapes,
	updateIntervalInState,
	updateSymbolInState,
} from "../state-helpers";
import {
	findTimingMomentumStudyId,
	isPanelWithCustomIndicator,
	isStudyIncluded,
} from "../studies-helpers";

const saveChartState = (
	tvWidget: IChartingLibraryWidget,
	onStateChange: OnStateChange,
) => {
	tvWidget.save((state: object) => {
		const filteredState = removeAllShapes(state);
		onStateChange(filteredState as Record<string, unknown>);
	});
};

export const updateState = async (
	tvWidget: IChartingLibraryWidget,
	chartState: ChartState,
) => {
	let { ticker, timeFrame, ...stateToLoad } = chartState;
	if (Object.keys(stateToLoad).length && stateToLoad.charts?.length) {
		stateToLoad = updateSymbolInState(
			stateToLoad as LibraryChartState,
			ticker || DEFAULT_TICKER,
			"",
		);
		stateToLoad = updateIntervalInState(
			stateToLoad as LibraryChartState,
			getResolutionKeyByValue(timeFrame || DEFAULT_STATE_VALUES.timeFrame)!,
		);
		stateToLoad = filterStudiesByPanelValues(
			[...(chartState.panels ?? []), ...(chartState.datasets ?? [])],
			stateToLoad,
		);
		stateToLoad = removeAllShapes(stateToLoad);

		tvWidget.load(stateToLoad);
	}

	tvWidget.chart().getSeries().setChartStyleProperties(1, {
		upColor: chartColors.up,
		downColor: chartColors.down,
		borderUpColor: chartColors.up,
		borderDownColor: chartColors.down,
		wickUpColor: chartColors.up,
		wickDownColor: chartColors.down,
	});

	for (const panel of [
		...(chartState.panels ?? []),
		...(chartState.datasets ?? []),
	]) {
		if (!isStudyIncluded(stateToLoad, panel)) {
			const studyId = await tvWidget
				.activeChart()
				.createStudy(panel, false, false);

			if (panel === "Timing Momentum") {
				tvWidget.activeChart().dataReady(() => {
					createTimingMomentumText(tvWidget.activeChart(), studyId!);
				});
			}
		} else if (panel === "Timing Momentum") {
			setTimeout(() => {
				const studyId = findTimingMomentumStudyId(chartState);
				if (studyId) {
					tvWidget.activeChart().dataReady(() => {
						createTimingMomentumText(tvWidget.activeChart(), studyId);
					});
				}
			}, 3000);
		}
	}
};

export const setSubscriptions = (
	tvWidget: IChartingLibraryWidget,
	chartState: ChartState,
	onStateChange: OnStateChange,
) => {
	tvWidget
		.activeChart()
		.onSymbolChanged()
		// @ts-ignore: IChartWidgetApi defines the subscribe method type as "() => void", but it actually accepts symbolInfo object
		.subscribe(null, (symbolInfo: LibrarySymbolInfo) => {
			onStateChange({ ticker: symbolInfo.ticker });
		});

	tvWidget
		.activeChart()
		.onIntervalChanged()
		.subscribe(null, (interval) => {
			onStateChange({ timeFrame: ResolutionMap[interval] });
		});

	tvWidget.subscribe("onAutoSaveNeeded", () => {
		saveChartState(tvWidget, onStateChange);
	});

	tvWidget.subscribe("undo_redo_state_changed", (state: UndoRedoState) => {
		if (state.undoText?.startsWith("remove")) {
			const redundantTextLength = 7;
			const panelName = state.undoText?.slice(redundantTextLength);

			if (isPanelWithCustomIndicator(panelName)) {
				const newPanels = chartState.panels?.filter(
					(panel) => panel !== panelName,
				);
				const newDatasets = chartState?.datasets?.filter(
					(panel) => panel !== panelName,
				);
				onStateChange({
					panels: newPanels,
					applyPanels: newPanels,
					datasets: newDatasets,
					applyDatasets: newDatasets,
				});
			}
		}
	});

	const debouncedSaveState = pDebounce(saveChartState, 1000);

	tvWidget
		.activeChart()
		.getTimeScale()
		.barSpacingChanged()
		.subscribe(null, () => debouncedSaveState(tvWidget, onStateChange));
};

export const setDefaultRange = (
	tvWidget: IChartingLibraryWidget,
	chartState: ChartState,
) => {
	if (!chartState?.charts?.length) {
		tvWidget.activeChart().setVisibleRange({
			from: (Date.now() - ONE_YEAR_IN_MS) / 1000,
			to: Date.now() / 1000,
		});
	}
};

export const drawAllReports = (
	tvWidget: IChartingLibraryWidget,
	interval: ResolutionString,
	ticker: string,
	emReports: Set<UTCTimestamp>,
) => {
	setTimeout(() => {
		if (!TIMEFRAME_MAP[ResolutionMap[interval] as Timeframes].isIntraday) {
			const activeChart = tvWidget.activeChart();
			const timeShiftInYears = interval === "1W" ? 6 : 1.2;
			const visibleTimeRange = {
				from: Date.now() - ONE_YEAR_IN_MS * timeShiftInYears,
				to: Date.now(),
			};

			activeChart.onDataLoaded().subscribe(null, () => {
				const currentVisibleTimeRange = activeChart.getVisibleRange();
				drawEMReports(
					activeChart,
					{
						from: currentVisibleTimeRange.from * 1000,
						to: currentVisibleTimeRange.to * 1000,
					},
					ticker,
					emReports,
				);
			});
			activeChart.dataReady(() => {
				drawEMReports(activeChart, visibleTimeRange, ticker, emReports);
			});
		}
	}, 1000);
};
